import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import { EyeOff, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { getLocalizedName } from '@/lib/utils';
import { useTranslation as useI18n } from 'react-i18next';

// eslint_disable-next-line @typescript-eslint/no-explicit-any
type FormValues = Record<string, any>;

interface UserFormFieldsProps {
  form: UseFormReturn<FormValues>;
  isEdit: boolean;
  onSubmit: (values: FormValues) => void;
}

export function UserFormFields({ form, isEdit, onSubmit }: UserFormFieldsProps) {
  const { t } = useTranslation();
  const { i18n } = useI18n();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="mt-4 flex flex-col gap-4"
        noValidate
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('name')}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t('enterName')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('phone')}</FormLabel>
              <FormControl>
                <PatternFormat
                  {...field}
                  format="+998 (##) ### ## ##"
                  mask="_"
                  placeholder={t('enterPhone')}
                  className={`w-full rounded-md px-2 py-1 border ${
                    form.formState.errors.phone ? 'border-red-500' : 'border-input'
                  }`}
                  onValueChange={(vals) => field.onChange(vals.formattedValue)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('email')}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t('enterEmail')} type="email" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {!isEdit && (
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('password')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('enterPassword')}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6"
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="roleId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('role')}</FormLabel>
              <FormControl>
                <UniversalCombobox
                  url="/roles"
                  listUrl="/roles/select-box"
                  labelKey="name"
                  valueKey="id"
                  selectedId={field.value}
                  onSelectItem={(item) => field.onChange(item?.id ?? null)}
                  renderLabel={(item) =>
                    getLocalizedName(
                      item as { name: string; name_en?: string; name_ru?: string },
                      i18n.language
                    )
                  }
                  placeholder={t('selectRole')}
                  clientSideFilter
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="hiredAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('hiredAt')}</FormLabel>
              <FormControl>
                <DateTimePicker value={field.value} onChange={field.onChange} mode="date" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="salary"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('salary')}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === '' ? undefined : Number(e.target.value)
                    )
                  }
                  placeholder={t('enterSalary')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {/* The KPI hours norm is agreed per MONTH (client review AL-2026/08-04-01,
            K-05). A period shorter than a month is prorated by working days, so
            this figure never has to be restated for a partial month. */}
        <FormField
          control={form.control}
          name="monthlyExpectedHours"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('monthlyExpectedHours')}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={1}
                  max={744}
                  step="1"
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === '' ? undefined : Number(e.target.value)
                    )
                  }
                  placeholder={t('enterMonthlyExpectedHours')}
                />
              </FormControl>
              <FormDescription>{t('monthlyExpectedHoursHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('status.status')}</FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('selectStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">{t('active')}</SelectItem>
                    <SelectItem value="BLOCKED">{t('blocked')}</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
